import React from 'react';
import { LegalDetail } from '../../types/api';
import { formatOfferAcceptedAt } from '../../config/legal';
import { useLanguage } from '../../contexts/LanguageContext';

interface LegalPanelProps {
  legal: LegalDetail | null | undefined;
  /** В настройки, на раздел «Реквизиты». */
  onEdit: () => void;
}

/**
 * Юридические реквизиты заведения и акцепт оферты — сводка на главной.
 *
 * Правятся они в настройках профиля (раздел «Реквизиты»): все поля
 * необязательные, партнёр заполняет их, когда дело дошло до договора, любым
 * подмножеством. Поэтому показываем только заполненное, а пока не заполнено
 * ничего — одну строку о том, что это необязательно.
 *
 * Подпись идентификатора (БИН или ИИН) приходит с сервера: кабинет и админка
 * не должны держать каждый свой словарь «ИП → ИИН, остальные → БИН».
 */
export const LegalPanel: React.FC<LegalPanelProps> = ({ legal, onEdit }) => {
  const { t } = useLanguage();

  const rows = [
    { key: t.cabinet.legalForm, value: legal?.form_label },
    { key: t.cabinet.legalName, value: legal?.name },
    { key: legal?.identifier_label ?? '', value: legal?.registration_number },
    { key: t.cabinet.legalAddress, value: legal?.address },
  ].filter((row): row is { key: string; value: string } => !!row.value);

  const offer = legal?.offer ?? null;

  return (
    <section className="qp-panel">
      <header className="qp-panel-head">
        <h2 className="qp-label">{t.cabinet.legalTitle}</h2>
        <button type="button" className="qp-link" onClick={onEdit}>
          {rows.length > 0 ? t.cabinet.legalEdit : t.cabinet.legalFill}
        </button>
      </header>

      {rows.length > 0 ? (
        <div className="qp-hairlist">
          {rows.map((row) => (
            <div key={row.key} className="qp-fact">
              <span className="qp-fact-k">{row.key}</span>
              <span className="qp-fact-v" title={row.value}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="qp-faint" style={{ margin: 0, fontSize: 13 }}>
          {t.cabinet.legalEmpty}
        </p>
      )}

      {offer && (
        <p className="qp-faint" style={{ margin: '10px 0 0', fontSize: 12 }}>
          {t.cabinet.legalOfferAccepted} {formatOfferAcceptedAt(offer.accepted_at)} · {t.cabinet.legalOfferEdition}{' '}
          {offer.version}
        </p>
      )}
    </section>
  );
};
